import { EntityType, GetModelsResponse, WildcardDetail } from "@/api/sdk"
import LoadingLogo from "@/components/LoadingLogo"
import { useDebounce, useScreen, useToast } from "@/hooks"
import { googleAnalytics } from "@/lib/gtag"
import { useBrowserRouter } from "@/providers/BrowserRouterProvider"
import { useAuth } from "@/providers/authContext"
import { useCreateTaskMutation, useCreateTaskNotAuthMutation, useTrackingViewMutation } from "@/queries"
import {
  useAddRecipesMutation,
  useGetRecipeDetailNotAuthQuery,
  useGetRecipeDetailQuery,
} from "@/queries/workspace/recipe"
import {
  useManagementErrorsStore,
  useLatestRecipesParams,
  useRecipeImageStore,
  useSignInStore,
  useTempTaskStoreV2,
} from "@/stores"
import generateCaptchaToken from "@/utils/generateCaptchaToken"
import { useQueryClient } from "@tanstack/react-query"
import classNames from "classnames"
import { AnimatePresence, motion } from "framer-motion"
import { uniqBy } from "lodash"
import _isNil from "lodash/isNil"
import _omitBy from "lodash/omitBy"
import Head from "next/head"

import { chunkString } from "@/utils/parser"
import useCustomRouter from "@/hooks/useCustomRouter"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import MultiImagesSelectBar from "../../FoldersV2/MultiImagesSelectBar"
import { useMultiImagesSelect } from "../../FoldersV2/useMultiImagesSelect"
// import { TurboModeProvder, useTurboMode } from "../../TurboMode/useTurboMode"
import NotFoundUI from "@/components/NotFound"
import RequestModal from "../../ImageDetailWorkspace/RequestModal"
import ExampleImages from "./ExampleImages"
import { CheckableRecipeInput } from "./FormDetail"
import { useLoraQuery } from "./FormDetail/FormDetailLora"
import ListRecipes from "./ListRecipes"
import RecipeSearch from "./RecipeSearch"
import SettingRecipe from "./SettingRecipe"
import {
  getStyleParamWithQueryClient,
  handlePasteParamsRecipe,
  RecipeCreateType,
  recipeParamsWithWildcard,
  validateRecipeParams,
} from "@/utils/task"
import WorkspaceContainer from "../../WorkspaceContainer"

export { type CheckableRecipeInput }

interface RecipeDetailProps {
  recipeId: string
}

const RecipeDetailInner = ({ recipeId }: RecipeDetailProps) => {
  const [sendRecipe, setSendRecipe] = useState(recipeId)
  const [recipeImage, setRecipeImage] = useRecipeImageStore(state => [state.recipeImage, state.setRecipeImage])
  const { tempTask, setTempTask } = useTempTaskStoreV2()
  const setSignInModal = useSignInStore(state => state.setSignInModal)
  const { user, loading } = useAuth()
  const [isAdding, setIsAdding] = useState(true)

  useEffect(() => {
    if (!recipeId) return

    setSendRecipe(recipeId)
  }, [recipeId])

  const {
    data: recipeNotAuth,
    isLoading: isLoadingNotAuth,
    isSuccess: isSuccessNotAuth,
    error: errorNotAuth,
    refetch: refetchNotAuth,
    isError: isLoadingNotAuthError,
  } = useGetRecipeDetailNotAuthQuery({ variables: { recipeId: sendRecipe }, enabled: !user && !loading && !isAdding })

  const {
    data: recipeAuth,
    isLoading: isLoadingAuth,
    isSuccess: isSuccessAuth,
    error: errorAuth,
    refetch: refetchAuth,
    isError: isLoadingAuthError,
  } = useGetRecipeDetailQuery({ variables: { recipeId: sendRecipe }, enabled: !!user && !loading && !isAdding })

  const recipe = useMemo(() => (user ? recipeAuth : recipeNotAuth), [recipeAuth, recipeNotAuth, user])
  const isLoading = isLoadingAuth || isLoadingNotAuth
  const isError = isLoadingAuthError || isLoadingNotAuthError
  const isSuccess = isSuccessAuth || isSuccessNotAuth
  const errorCode = user ? errorAuth : errorNotAuth
  const statusErrorCode = Number((errorCode as any)?.statusCode)
  const refetch = user ? refetchAuth : refetchNotAuth

  const isOpenRequest = useDebounce(
    !isLoading && !!recipe && !recipe.capabilities?.canView && !(recipe.isPublic || recipe.discoverableAt),
    500,
  )

  const form = useForm<RecipeCreateType>({
    mode: "onSubmit",
  })

  const [isOpenSideBar, setIsOpenSidebar] = useState(true)
  const [recipeInputs, setRecipeInputs] = useState<CheckableRecipeInput[]>([])
  const toast = useToast()
  const qc = useQueryClient()
  const { width } = useScreen()
  const router = useCustomRouter()

  const [loraIds, setLoraIds] = useState<string[]>([])
  const [wildcardsState, setWildcardsState] = useState<WildcardDetail[]>([])

  const { mutate: mutateTrackingView } = useTrackingViewMutation()
  const { latestRecipesParams, setLatestRecipesParams } = useLatestRecipesParams()

  const { mutateAsync: mutateAdd } = useAddRecipesMutation()

  const recipeInputsRef = useRef(recipeInputs)

  useEffect(() => {
    recipeInputsRef.current = recipeInputs
  }, [recipeInputs])

  useEffect(() => {
    if (isSuccess) {
      if (isSuccessAuth) {
        mutateTrackingView({
          entityId: recipe?.id ?? recipeId,
          entityType: EntityType.RECIPE,
        })
      }

      googleAnalytics.handleCategoryEvent({
        action: "view",
        params: {
          action: "View Recipe Detail",
          recipe_id: recipeId,
          recipe_name: recipe?.name ?? "",
        },
      })
    }
  }, [isSuccess])

  useEffect(() => {
    const handleAddRecipe = async (recipeId: string) => {
      const result = await mutateAdd({
        ids: [recipeId],
        userUid: user?.uid ?? "",
      }).finally(() => {
        setIsAdding(false)
      })
    }

    if (!loading && router.isReady) {
      handleAddRecipe(recipeId)
    }
  }, [loading, router.isReady])

  useEffect(() => {
    if (!recipe) return

    const timer = setTimeout(() => {
      let latestRecipeParams = latestRecipesParams?.find(i => i.recipeId === recipe.id)?.params

      const defaultValues = recipe.steps
        ?.map(step =>
          step.inputs.reduce((acc, curr) => {
            acc[curr.key] = latestRecipeParams?.[curr.key] || curr.value

            return acc
          }, {}),
        )
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})

      handlePasteParamsRecipe(defaultValues, form.reset, setRecipeInputs, recipe)
    }, 50)

    return () => {
      clearTimeout(timer)
    }
  }, [recipe])

  const { asPath } = useBrowserRouter()

  useEffect(() => {
    if (!recipe) return

    if (!asPath.includes("/workspace/tools/recipes/")) return
    if (!recipeImage) return

    if (!recipeImage.isModal) {
      const timeout = setTimeout(() => {
        if (recipeImage.params) {
          const { modelIds, ...newParams } = recipeImage.params

          setLoraIds(modelIds)

          handlePasteParamsRecipe(newParams, form.reset, setRecipeInputs, recipe, params => {
            if (recipeImage) {
              let paramWildcard = [] as WildcardDetail[]
              const paramsWildcards = Object.keys(params).filter(key => key.includes("_wildcard"))

              paramsWildcards.forEach(key => {
                const wildcard = params[key] as WildcardDetail
                const wildcardIndex = paramWildcard.findIndex(i => i.id === wildcard.id)

                if (wildcardIndex > -1) {
                  paramWildcard[wildcardIndex] = wildcard
                } else {
                  paramWildcard.push(wildcard)
                }
              })

              setWildcardsState(paramWildcard)

              setRecipeImage(null)
            }
          })
        }
      }, 400)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [recipeImage, recipe, asPath])

  const setErrorState = useManagementErrorsStore(state => state.setErrorState)

  const { mutate: mutateCreateTask, isPending: isLoadingCreateAuth } = useCreateTaskMutation({
    onSuccess: data => {
      qc.invalidateQueries({ queryKey: ["recipe-ongoing", { recipeIds: [sendRecipe] }] })

      if (width < 768) {
        setIsOpenSidebar(false)
      }

      toast({
        status: "success",
        title: "Submitted! Your task is running",
        duration: 5000,
      })

      const { prompt: promptParam, ...otherParams } = data.params as Record<string, any>

      const splitPrompt = chunkString(promptParam ?? "", 100)
      const promptParams = Object.fromEntries((splitPrompt ?? []).map((item, index) => [`prompt_${index + 1}`, item]))

      googleAnalytics.handleCategoryEvent({
        action: "click",
        params: {
          action: "Submit Generated Task Completed",
          recipe_id: recipeId,
          recipe_name: recipe?.name ?? "",
          task_id: data.id,
          ...otherParams,
          ...promptParams,
        },
      })
    },
    onError: (err: any) => {
      if (
        err?.message.startsWith("Guest user does not have permission") ||
        err?.message.startsWith("Your subscription has ended")
      ) {
        setErrorState({
          isOpen: true,
          message: err.message,
        })
        return
      }

      if (err?.error === "Bad Request") {
        toast({ title: "Cannot Complete Request", message: [err.message], status: "error" })
      } else {
        toast({ title: "Error", message: [err.message], status: "error" })
      }
    },
  })

  const { mutate: mutateCreateTaskNotAuth, isPending: isLoadingCreateNotAuth } = useCreateTaskNotAuthMutation({
    onSuccess: data => {
      if (data) {
        setTempTask([...(tempTask || []), { id: data.id, type: "recipe" }])
      }

      googleAnalytics.handleCategoryEvent({
        action: "click",
        params: {
          action: "Submit Generated Task Completed",
          recipe_id: recipeId,
          recipe_name: recipe?.name ?? "",
          task_id: data.id,
          ...data.params,
        },
      })

      qc.invalidateQueries({ queryKey: ["recipe-ongoing", { recipeIds: [sendRecipe] }] })

      if (width < 768) {
        setIsOpenSidebar(false)
      }

      toast({
        status: "success",
        title: "Submitted! Your task is running",
        duration: 5000,
      })
    },
    onError: (err: any) => {
      if (
        err?.message.startsWith("Guest user does not have permission") ||
        err?.message.startsWith("Your subscription has ended")
      ) {
        setErrorState({
          isOpen: true,
          message: err.message,
        })
        return
      }
      if (err?.error === "Bad Request") {
        toast({ title: "Cannot Complete Request", message: [err.message], status: "error" })
      } else {
        toast({ title: "Error", message: [err.message], status: "error" })
      }
    },
  })

  const isLoadingCreate = isLoadingCreateAuth || isLoadingCreateNotAuth

  const [isQueueingTurbo, setQueueingTurbo] = useState(false)
  const turboAuto = useRef(false)

  const handleChangeRecipe = (recipeId: string) => {
    turboAuto.current = false
    setSendRecipe(recipeId)
    router.replace(`/workspace/tools/recipes/${recipeId}`)
  }

  const onSaveParams = useCallback(
    (params: Record<string, any>, recipeId: string, folderId?: string) => {
      const prevLatestRecipeParams = [
        ...(latestRecipesParams ?? []),
        {
          folderId: folderId,
          recipeId,
          params,
        },
      ].reverse()

      setLatestRecipesParams(uniqBy(prevLatestRecipeParams, r => r.recipeId))
    },
    [latestRecipesParams, setLatestRecipesParams],
  )

  const [isMutating, setIsMutating] = useState(false)

  // submit
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!recipe) return

    const lorasKey = useLoraQuery.getKey({ baseModel: recipe.baseModel })
    const loras = qc.getQueryData<GetModelsResponse>(lorasKey)

    const res = await validateRecipeParams({
      form,
      recipeInputs,
      loras,
    })

    const hasWildcardField = recipeInputs.some(input => input.allowWildcard)

    if (!res) return

    const { params, folderId } = res

    const { prompt: promptParam, ...otherParams } = params

    const splitPrompt = chunkString(promptParam ?? "", 100)
    const promptParams = Object.fromEntries((splitPrompt ?? []).map((item, index) => [`prompt_${index + 1}`, item]))

    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Submit Generated Task",
        recipe_id: recipeId,
        recipe_name: recipe?.name ?? "",
        ...otherParams,
        ...promptParams,
      },
    })

    let newParams = params

    try {
      if (hasWildcardField) {
        setIsMutating(true)

        newParams = await recipeParamsWithWildcard(newParams, recipeInputs)
      }

      if (newParams?.["style"]) {
        setIsMutating(true)

        const style = await getStyleParamWithQueryClient(newParams["style"], qc)

        if (style && style.name) {
          newParams = {
            ...newParams,
            styleName: style.name,
          }
        }
      }
    } catch (error) {
      setIsMutating(false)
    } finally {
      setIsMutating(false)
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })

    onSaveParams(newParams, recipe.id, folderId)

    if (!user) {
      if (tempTask && tempTask?.length >= 5) {
        toast({
          status: "error",
          title: "You have reached the limit of 5 tasks",
          message: ["Please sign in to continue."],
        })

        setSignInModal({
          signIn: true,
        })

        return
      }

      const captchaToken = await generateCaptchaToken()

      mutateCreateTaskNotAuth({
        recipeId: recipe.id,
        params: newParams,
        captchaToken,
      })
      return
    }

    mutateCreateTask({
      folderId: folderId,
      recipeId: recipe.id,
      params: newParams,
    })
  }

  const handleRunAgain = async (folderId: string, recipeId: string, params: Record<string, any>) => {
    if (!user) {
      if (tempTask && tempTask?.length >= 5) {
        toast({
          status: "error",
          title: "Limit reached",
          message: ["Please sign in to generate more"],
        })

        setSignInModal({
          signIn: true,
        })

        return
      }

      const captchaToken = await generateCaptchaToken()

      mutateCreateTaskNotAuth({
        recipeId,
        params,
        captchaToken,
      })
      return
    }

    // read wildcard data
    // process wildcard prompt for only fields that are in params
    const hasWildcardField = recipeInputs.some(input => input.allowWildcard)

    let newParams = params

    if (hasWildcardField) {
      newParams = await recipeParamsWithWildcard(newParams, recipeInputs)
    }

    mutateCreateTask({
      folderId,
      recipeId,
      params: newParams,
    })
  }

  const { selectionMode, selectedItems, toggleSelectionMode, addSelection, clear } = useMultiImagesSelect()

  const hasPrompt = useMemo(() => recipeInputs.some(i => i.key === "prompt"), [recipeInputs])

  const renderBody = () => {
    if (loading || isLoading || isAdding) {
      return (
        <div className="flex items-center justify-center flex-1 w-full">
          <LoadingLogo />
        </div>
      )
    }

    if (isError) {
      if (statusErrorCode === 403)
        return (
          <RequestModal
            onRefetch={refetch}
            isModal
            dataRequest={{
              entityId: recipeId,
              entityType: EntityType.RECIPE,
            }}
            isOpenRequest={true}
            onCloseRequestModal={() => router.push("/workspace/tools/recipes")}
          />
        )

      return <NotFoundUI description="Recipe" redirectTo="/workspace/tools/recipes" />
    }

    return (
      <>
        <Head>
          <title>GAIA | {recipe?.name ?? "..."}</title>
        </Head>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative flex flex-1 w-full">
          <FormProvider {...form}>
            <form onSubmit={submit} className="w-full flex-1 flex">
              <div className="w-full flex-1 flex">
                <SettingRecipe
                  onResetDefaultSettings={() => {
                    if (!recipe) return

                    const defaultValues = recipe.steps
                      ?.map(step =>
                        step.inputs.reduce((acc, curr) => {
                          acc[curr.key] = curr.value

                          return acc
                        }, {}),
                      )
                      .reduce((acc, curr) => ({ ...acc, ...curr }), {})

                    handlePasteParamsRecipe(defaultValues, form.reset, setRecipeInputs, recipe)
                  }}
                  isOpenSideBar={isOpenSideBar}
                  isLoadingCreate={isLoadingCreate || isQueueingTurbo || isMutating}
                  setIsOpenSidebar={setIsOpenSidebar}
                  recipeInputs={recipeInputs}
                  setRecipeInputs={setRecipeInputs}
                  sendRecipe={sendRecipe}
                  setSendRecipe={handleChangeRecipe}
                  form={form}
                  recipe={recipe}
                  loraIds={loraIds}
                  wildcardsState={wildcardsState}
                />

                <div className="flex-1 flex items-center flex-col bg-black relative">
                  <MultiImagesSelectBar
                    clear={clear}
                    className={classNames("top-[3.75rem]", {
                      hidden: !user,
                    })}
                    recipeId={sendRecipe}
                    selectedItems={selectedItems}
                  />
                  <WorkspaceContainer className="flex-1">
                    <ExampleImages recipeId={sendRecipe} hasPrompt={hasPrompt} />
                    <RecipeSearch
                      className={classNames({
                        hidden: !user,
                      })}
                      sendRecipe={sendRecipe}
                      selectionMode={selectionMode}
                      toggleSelectionMode={toggleSelectionMode}
                    />
                    {!user && <div className="py-4" />}
                    <ListRecipes
                      isLoadingCreateTask={isLoadingCreate}
                      onCreateTask={({ folderId, recipeId, params }) => {
                        handleRunAgain(folderId, recipeId, params)
                      }}
                      onCloseSidebar={() => setIsOpenSidebar(false)}
                      recipeName={recipe?.name}
                      recipeInputs={recipeInputs}
                      setRecipeInputs={setRecipeInputs}
                      taskIds={!user ? tempTask?.map(i => i.id) : undefined}
                      setValues={params => handlePasteParamsRecipe(params, form.reset, setRecipeInputs, recipe)}
                      sendRecipe={sendRecipe}
                      selectionMode={selectionMode}
                      selectedItems={selectedItems}
                      onCheck={image => {
                        addSelection({
                          ...image,
                          capabilities: {
                            canMove: true,
                            canShare: true,
                            canDelete: true,
                            canUpdate: true,
                            canView: true,
                            canComment: true,
                            canRecover: true,
                            canToggleDiscovery: true,
                          },
                        })
                      }}
                    />
                  </WorkspaceContainer>
                </div>
              </div>
            </form>
          </FormProvider>

          <AnimatePresence mode="wait">
            {isOpenSideBar && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.2 },
                }}
                className={classNames("fixed lg:hidden inset-0 bg-blackAlpha-600 z-[23] md:z-[1]")}
                onClick={() => setIsOpenSidebar(false)}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </>
    )
  }

  return (
    <>
      <div className="flex flex-col w-full flex-1 relative">{renderBody()}</div>
      {recipe && (
        <RequestModal
          onRefetch={refetch}
          isModal
          dataRequest={{
            entityId: recipe.id,
            entityType: EntityType.RECIPE,
            workspace: { ...recipe.workspace, picture: recipe.workspace?.picture ?? "" },
            discoverableAt: recipe?.discoverableAt,
          }}
          isOpenRequest={isOpenRequest}
          onCloseRequestModal={() => router.push("/workspace/tools/recipes")}
        />
      )}
    </>
  )
}

const RecipeDetail = (props: RecipeDetailProps) => {
  return <RecipeDetailInner {...props} />
}

export default RecipeDetail
